"use client";

import React, { FC } from "react";

interface ShopTheEditProps {
  prefix: string;
  eyebrow?: string;
  heading?: string;
  subtitle?: string;
  ctaLabel?: string;
  /** Seller-customized tiles. When provided, these replace the default TILES.
   *  A tile with `productIds` links to a curated collection of those products;
   *  otherwise `href` overrides the default "all products" destination. */
  tiles?: {
    eyebrow: string;
    title: string;
    description: string;
    href?: string;
    productIds?: string[];
  }[];
}

interface EditTile {
  eyebrow: string;
  title: string;
  description: string;
  gradient: string;
  glow: string;
}

const TILES: EditTile[] = [
  {
    eyebrow: "Just landed",
    title: "New in",
    description: "Fresh picks and seasonal pieces this month.",
    gradient: "linear-gradient(135deg, var(--coral) 0%, var(--coral-deep) 100%)",
    glow: "rgba(255, 255, 255, 0.45)",
  },
  {
    eyebrow: "Smart buys",
    title: "Under ₹500",
    description: "Little luxuries that won't break the bank.",
    gradient: "linear-gradient(135deg, #134B7E 0%, var(--navy) 100%)",
    glow: "rgba(242, 98, 80, 0.55)",
  },
  {
    eyebrow: "For someone special",
    title: "Gifting",
    description: "Beautifully packaged, ready to give.",
    gradient: "linear-gradient(135deg, #4A1F47 0%, #15203F 100%)",
    glow: "rgba(242, 98, 80, 0.5)",
  },
];

const ArrowIcon: FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);

// Default visual palette (gradient + glow) cycled across seller-provided tiles.
const PALETTE = TILES.map((t) => ({ gradient: t.gradient, glow: t.glow }));

export const ShopTheEdit: FC<ShopTheEditProps> = ({
  prefix,
  eyebrow,
  heading,
  subtitle,
  ctaLabel,
  tiles,
}) => {
  const href = `/shop/all-products?prefix=${prefix}`;

  const collectionHref = (t: { title: string; href?: string; productIds?: string[] }) =>
    t.productIds && t.productIds.length
      ? `/shop/all-products?prefix=${encodeURIComponent(prefix)}&ids=${t.productIds.join(
          ","
        )}&title=${encodeURIComponent(t.title)}`
      : t.href || href;

  const resolvedTiles =
    tiles && tiles.length > 0
      ? tiles.map((t, i) => ({
          eyebrow: t.eyebrow,
          title: t.title,
          description: t.description,
          href: collectionHref(t),
          gradient: PALETTE[i % PALETTE.length].gradient,
          glow: PALETTE[i % PALETTE.length].glow,
        }))
      : TILES.map((t) => ({ ...t, href }));

  return (
    <section className="mt-[clamp(40px,5vw,64px)]">
      {/* Header */}
      <div className="mb-6 flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-end sm:gap-4">
        <div>
          <span
            className="mb-2.5 inline-flex items-center gap-2 text-[12px] font-bold uppercase"
            style={{ color: "var(--coral-deep)", letterSpacing: "0.1em" }}
          >
            <span
              style={{
                width: 18,
                height: 9,
                borderRadius: "999px 999px 0 0",
                background: "var(--coral)",
                display: "inline-block",
              }}
            />
            {eyebrow || "Curated"}
          </span>
          <h2
            className="leading-tight"
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 600,
              fontSize: "clamp(20px,2.6vw,30px)",
              letterSpacing: "-0.01em",
              color: "var(--techno-black)",
            }}
          >
            {heading || "Shop the edit"}
          </h2>
          <p
            className="mt-1.5 text-[14px]"
            style={{ color: "var(--techno-black-70)" }}
          >
            {subtitle || "Hand-picked collections to make discovery effortless"}
          </p>
        </div>

        <a
          href={href}
          className="group inline-flex shrink-0 items-center gap-1.5 self-start text-[13.5px] font-semibold outline-none transition-colors duration-150 hover:text-[color:var(--coral-deep)] focus-visible:underline focus-visible:underline-offset-4 sm:self-auto"
          style={{ color: "var(--navy)" }}
        >
          All collections
          <span className="transition-transform duration-150 group-hover:translate-x-0.5">
            <ArrowIcon />
          </span>
        </a>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-3">
        {resolvedTiles.map((tile, i) => (
          <a
            key={i}
            href={tile.href}
            aria-label={`${tile.title} — ${tile.description}`}
            className="group relative flex min-h-[220px] flex-col justify-between overflow-hidden p-6 outline-none transition-all duration-200 ease-out hover:-translate-y-1 focus-visible:-translate-y-1"
            style={{
              borderRadius: "var(--radius-lg)",
              background: tile.gradient,
              color: "#fff",
              boxShadow: "var(--shadow-soft)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "var(--shadow-hover)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "var(--shadow-soft)";
            }}
            onFocus={(e) => {
              e.currentTarget.style.boxShadow = "var(--shadow-hover)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.boxShadow = "var(--shadow-soft)";
            }}
          >
            {/* Rising-Sun-Arc coral glow motif */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -right-12 -top-12 transition-transform duration-300 ease-out group-hover:scale-110"
              style={{
                width: 180,
                height: 180,
                borderRadius: "999px",
                background: `radial-gradient(circle at 50% 50%, ${tile.glow}, transparent 68%)`,
              }}
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -bottom-16 -left-10"
              style={{
                width: 150,
                height: 150,
                borderRadius: "999px",
                background:
                  "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.10), transparent 70%)",
              }}
            />

            {/* Content */}
            <div className="relative">
              <span
                className="inline-block text-[11px] font-bold uppercase"
                style={{
                  letterSpacing: "0.12em",
                  color: "rgba(255,255,255,0.85)",
                }}
              >
                {tile.eyebrow}
              </span>
              <h3
                className="mt-2 leading-tight"
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 600,
                  fontSize: "clamp(24px,2.4vw,30px)",
                  letterSpacing: "-0.01em",
                  color: "#fff",
                }}
              >
                {tile.title}
              </h3>
              <p
                className="mt-2 max-w-[24ch] text-[13.5px] leading-snug"
                style={{ color: "rgba(255,255,255,0.82)" }}
              >
                {tile.description}
              </p>
            </div>

            {/* Pill button */}
            <div className="relative mt-5">
              <span
                className="inline-flex min-h-[44px] items-center gap-1.5 px-4 text-[13px] font-semibold transition-all duration-150 group-hover:gap-2.5"
                style={{
                  borderRadius: "999px",
                  background: "rgba(255,255,255,0.16)",
                  border: "1px solid rgba(255,255,255,0.28)",
                  backdropFilter: "blur(4px)",
                  color: "#fff",
                }}
              >
                {ctaLabel || "Explore"}
                <span className="transition-transform duration-150 group-hover:translate-x-0.5">
                  <ArrowIcon />
                </span>
              </span>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
};
